import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { billingApi, BillingDetails, formatInrFromPaise } from '../api/billing.api';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import UserMenu from '../components/UserMenu';
import { COMPANY } from '../legal/company';
import { useNotify } from '../context/NotifyContext';
import usePageMeta from '../hooks/usePageMeta';
import useInvoiceDownload from '../hooks/useInvoiceDownload';

const emptyBilling = (seed?: Partial<BillingDetails> | null): BillingDetails => ({
  legalName: seed?.legalName || '',
  email: seed?.email || '',
  phone: seed?.phone || '',
  addressLine1: seed?.addressLine1 || '',
  addressLine2: seed?.addressLine2 || '',
  city: seed?.city || '',
  state: seed?.state || '',
  pincode: seed?.pincode || '',
  gstin: seed?.gstin || '',
});

const BillingPage: React.FC = () => {
  usePageMeta({ title: 'Billing — OnTheWeb', index: false });

  const navigate = useNavigate();
  const { notify } = useNotify();
  const { download, downloadingId } = useInvoiceDownload();
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: billingApi.listInvoices,
  });

  const [billing, setBilling] = useState<BillingDetails>(emptyBilling());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    billingApi
      .getBillingProfile()
      .then((data) => setBilling(emptyBilling(data)))
      .catch(() => {});
  }, []);

  const field = (key: keyof BillingDetails, label: string, required = true) => (
    <label className="block">
      <span className="nova-label">
        {label}
        {required ? '' : ' (optional)'}
      </span>
      <input
        className="nova-input"
        value={billing[key] || ''}
        onChange={(e) => setBilling({ ...billing, [key]: e.target.value })}
        required={required}
      />
    </label>
  );

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await billingApi.saveBillingProfile({
        ...billing,
        gstin: billing.gstin?.trim() || undefined,
        addressLine2: billing.addressLine2?.trim() || undefined,
      });
      setBilling(emptyBilling(saved));
      notify.success('Billing details saved.');
    } catch (err: any) {
      notify.error(err.response?.data?.message || 'Could not save billing details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nova-page">
      <nav className="nova-nav relative border-b border-nova-border">
        <div className="nova-nav-inner">
          <button type="button" onClick={() => navigate('/dashboard')} className="min-w-0">
            <Logo />
          </button>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-10 pt-24">
        <h1 className="font-display text-3xl font-extrabold text-nova-text mb-2">Billing</h1>
        <p className="text-nova-text2 mb-8">Your billing details and tax invoices from {COMPANY.legalName}.</p>

        <h2 className="font-display text-xl font-bold text-nova-text mb-3">Billing information</h2>
        <form onSubmit={handleSave} className="nova-card grid sm:grid-cols-2 gap-4 mb-12">
          {field('legalName', 'Billing name')}
          {field('email', 'Billing email')}
          {field('phone', 'Phone')}
          {field('gstin', 'GSTIN', false)}
          {field('addressLine1', 'Address')}
          {field('addressLine2', 'Address line 2', false)}
          {field('city', 'City')}
          {field('state', 'State')}
          {field('pincode', 'Pincode')}
          <div className="sm:col-span-2">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save billing details'}
            </button>
          </div>
        </form>

        <h2 className="font-display text-xl font-bold text-nova-text mb-3">Invoices</h2>
        {isLoading ? (
          <p className="text-nova-text2">Loading invoices...</p>
        ) : invoices.length === 0 ? (
          <p className="text-nova-text3">No invoices yet. Publish a website to generate the first one.</p>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <div key={invoice._id} className="nova-card flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <Link to={`/invoices/${invoice._id}`} className="font-semibold text-nova-text hover:text-nova-sky">
                    {invoice.invoiceNumber}
                  </Link>
                  <p className="text-sm text-nova-text3">
                    {invoice.websiteName || invoice.plan} · {new Date(invoice.paidAt).toLocaleDateString('en-IN')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-nova-text">{formatInrFromPaise(invoice.totalPaise)}</span>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={downloadingId === invoice._id}
                    onClick={() => download(invoice._id, invoice.invoiceNumber)}
                  >
                    {downloadingId === invoice._id ? 'Preparing...' : 'Download PDF'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
